import axios from "axios";
import React, { useState, useEffect } from "react";
import { BASE_URL } from "../../../env";
import Sidebar from "../../../components/layout/Sidebar";
import { Form, Row, Col } from "react-bootstrap";
import {
  getFirstAndLastEmiDates,
  calculateEmiAmount,
  calculateInsuranceAmount,
  isLoanCovered,
  formatAmount,
  resolveEffectiveStartDate,
} from "../../../utils/utils";
import CustomPagination from "../../../components/ui/CustomPagination";
import DataTable from "../../../components/ui/DataTable";
import { useAdminAuth } from "../../auth/AdminAuthProvider";
import DashboardHeader from "../../../components/layout/DashboardHeader";
import { MdExpandMore } from "react-icons/md";
import StandardizedDateInput from "../../../components/ui/StandardizedDateInput";

const InsuranceReport = () => {
  const { adminUser } = useAdminAuth();
  const [reportData, setReportData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);

  const [openSidebarToggle, setOpenSidebarToggle] = useState(window.innerWidth > 1000);
  const [currentPage, setCurrentPage] = useState(1);
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [searchTerm, setSearchTerm] = useState("");

  const itemsPerPage = 20;

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  const totalPages = Math.ceil(filteredData.length / itemsPerPage);

  const paginationData = filteredData.slice(indexOfFirstItem, indexOfLastItem);

  const OpenSidebar = () => {
    setOpenSidebarToggle(!openSidebarToggle);
  };

  useEffect(() => {
    const getReportData = async (id) => {
      try {
        const response = await axios.get(
          `${BASE_URL}/api/getInsuranceReportData`,
          { params: { id } }
        );
        // console.log(
        //   "MasterData response.data: " + JSON.stringify(response.data)
        // );
        if (response.data) {
          setReportData(response.data);
        } else {
          console.log("Error in fetching Data");
        }
      } catch (error) {
        console.log("Error fetching Data:", error);
      }
    };

    getReportData(adminUser.user.id);
  }, [adminUser.user.id]);

  // Automatically filter data when fromDate, toDate, or searchTerm changes
  useEffect(() => {
    // Process all data first
    const processedData = reportData.map((item) => {
      const emiMonthsPaid = item.receiptsDetails
        ? new Set(item.receiptsDetails.filter(
          (receipt) =>
            (!fromDate || new Date(receipt.emiDate) >= new Date(fromDate)) &&
            (!toDate || new Date(receipt.emiDate) <= new Date(toDate)) &&
            receipt.status === "paid"
        ).map(receipt => receipt.emiDate)).size
        : 0;

      return {
        ...item,
        emiMonthsPaid,
      };
    });

    let filtered = processedData;

    if (fromDate && toDate) {
      const start = new Date(fromDate);
      start.setHours(0, 0, 0, 0);
      const end = new Date(toDate);
      end.setHours(23, 59, 59, 999);

      filtered = filtered.filter((item) => {
        if (!item.branchManagerStatusUpdatedAt) return false;
        const updatedAt = new Date(item.branchManagerStatusUpdatedAt);
        return updatedAt >= start && updatedAt <= end;
      });
    }

    if (searchTerm && searchTerm.trim() !== "") {
      const term = searchTerm.trim().toLowerCase();
      filtered = filtered.filter((item) => {
        const memberName = String(item?.memberName || "").toLowerCase();
        const customerId = String(item?.customerId || "").toLowerCase();
        const loanId = String(item?.loanId || "").toLowerCase();
        const phoneNumber = String(item?.phoneNumber || "").toLowerCase();
        const aadharNo = String(item?.aadharNo || "").toLowerCase();
        const applicationId = String(item?.ApplicationId || "").toLowerCase();

        return (
          memberName.includes(term) ||
          customerId.includes(term) ||
          loanId.includes(term) ||
          phoneNumber.includes(term) ||
          aadharNo.includes(term) ||
          applicationId.includes(term)
        );
      });
    }

    setFilteredData(filtered);
    setCurrentPage(1);
  }, [fromDate, toDate, reportData, searchTerm]);

  // Define columns with label and render logic
  const columns = [
    { label: "S No", render: (data, index) => index + 1 },
    { label: "Branch Code", render: (data) => data.branchCode },
    {
      label: "Sub Category",
      render: (data) => data.businessDetails.natureOfBusiness,
    },
    { label: "Loan Account No", render: (data) => data.loanId },
    { label: "Serial Number", render: (data) => data.loanCycle },
    { label: "Member Name", render: (data) => data.memberName },
    {
      label: "Member Guardian Name",
      render: (data) => data.familyMember.coApplicantName,
    },
    { label: "Member DOB", render: (data) => data.dateOfBirth },
    { label: "Gender", render: (data) => data.gender },
    { label: "Document Type", render: (data) => "Aadhar" },
    { label: "Document Number", render: (data) => data.aadharNo },
    { label: "Relationship from Primary", render: (data) => "Self" },
    {
      label: "Occupation",
      render: (data) => data.businessDetails.natureOfBusiness,
    },
    { label: "Mobile No", render: (data) => data.phoneNumber },
    { label: "Email", render: (data) => "" },
    {
      label: "Nominee Name",
      render: (data) => data.familyMember.coApplicantName,
    },
    {
      label: "Nominee DOB",
      render: (data) => data.familyMember.dateOfBirth,
    },
    {
      label: "Nominee Age",
      render: (data) => {
        const dateOfBirth = new Date(data.familyMember.dateOfBirth); // Parse the dateOfBirth
        const today = new Date(); // Current date
        let age = today.getFullYear() - dateOfBirth.getFullYear(); // Calculate the difference in years

        // Adjust if the birthdate hasn't occurred this year
        const hasHadBirthday =
          today.getMonth() > dateOfBirth.getMonth() ||
          (today.getMonth() === dateOfBirth.getMonth() &&
            today.getDate() >= dateOfBirth.getDate());
        if (!hasHadBirthday) {
          age--;
        }

        return age; // Return the calculated age
      },
    },
    {
      label: "Nominee Gender",
      render: (data) => data.familyMember.gender,
    },
    {
      label: "Nominee Relation",
      render: (data) => data.familyMember.relation,
    },
    {
      label: "Nominee Minor",
      render: (data) => {
        const dateOfBirth = new Date(data.familyMember.dateOfBirth); // Parse the dateOfBirth
        const today = new Date(); // Current date
        let age = today.getFullYear() - dateOfBirth.getFullYear(); // Calculate the difference in years

        // Adjust if the birthdate hasn't occurred this year
        const hasHadBirthday =
          today.getMonth() > dateOfBirth.getMonth() ||
          (today.getMonth() === dateOfBirth.getMonth() &&
            today.getDate() >= dateOfBirth.getDate());
        if (!hasHadBirthday) {
          age--;
        }

        return age > 18 ? "NO" : "YES";
      },
    },
    { label: "Nominee Guardian Name", render: (data) => data.memberName },
    {
      label: "Nominee Guardian Age",
      render: (data) => {
        const dateOfBirth = new Date(data.dateOfBirth); // Parse the dateOfBirth
        const today = new Date(); // Current date
        let age = today.getFullYear() - dateOfBirth.getFullYear(); // Calculate the difference in years

        // Adjust if the birthdate hasn't occurred this year
        const hasHadBirthday =
          today.getMonth() > dateOfBirth.getMonth() ||
          (today.getMonth() === dateOfBirth.getMonth() &&
            today.getDate() >= dateOfBirth.getDate());
        if (!hasHadBirthday) {
          age--;
        }

        return age; // Return the calculated age
      },
    },
    {
      label: "Nominee Guardian Relationship with Nominee",
      render: (data) => {
        const { relation, gender } = data.familyMember;

        if (relation === "Son") {
          return gender === "male" ? "Father" : "Mother";
        } else if (relation === "Daughter") {
          return gender === "male" ? "Father" : "Mother";
        } else if (relation === "Spouse") {
          return "Spouse";
        } else if (relation === "Father") {
          return gender === "male" ? "Son" : "Daughter";
        } else if (relation === "Mother") {
          return gender === "male" ? "Son" : "Daughter";
        } else if (relation === "Brother") {
          return gender === "male" ? "Brother" : "Sister";
        } else if (relation === "Father-in-Law") {
          return gender === "male" ? "Son-in-Law" : "Daughter-in-Law";
        } else if (relation === "Mother-in-Law") {
          return gender === "male" ? "Son-in-Law" : "Daughter-in-Law";
        }

        // Default return if no match is found
        return "Unknown";
      },
    },
    { label: "Master Policy No", render: (data) => "" },
    {
      label: "Loan Amount",
      render: (data) => formatAmount(data.sanctionedLoanAmountBySanctionCommittee || 0),
    },
    {
      label: "Start Date",
      render: (data) => {
        const { firstEmiDate } = getFirstAndLastEmiDates(
          data.branchManagerStatusUpdatedAt,
          data.proposedLoanDetails.tenureInMonths,
          data.emiDateByBranchManager,
          resolveEffectiveStartDate(data)
        );
        return firstEmiDate ? firstEmiDate.toLocaleDateString("en-GB", {
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
        }) : "";
      },
    },
    {
      label: "Maturity Date",
      render: (data) => {
        const { lastEmiDate } = getFirstAndLastEmiDates(
          data.branchManagerStatusUpdatedAt,
          data.proposedLoanDetails.tenureInMonths,
          data.emiDateByBranchManager,
          resolveEffectiveStartDate(data)
        );
        return lastEmiDate ? lastEmiDate.toLocaleDateString("en-GB", {
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
        }) : "";
      },
    },
    {
      label: "Premium",
      render: (data) =>
        formatAmount(
          isLoanCovered(data)
            ? calculateInsuranceAmount(
              data.sanctionedLoanAmountBySanctionCommittee,
              data.proposedLoanDetails.tenureInMonths
            )
            : 0
        ),
    },
    {
      label: "Sum Insured Loan Amount",
      render: (data) =>
        formatAmount(data.sanctionedLoanAmountBySanctionCommittee || 0),
    },
    {
      label: "Tenure In Months",
      render: (data) => data.proposedLoanDetails.tenureInMonths,
    },
    { label: "Loan Type", render: (data) => data.loanType },
    {
      label: "Address1",
      render: (data) =>
        `${data.permanentDoorNo},${data.permanentStreetNo},${data.permanentPanchayat},${data.permanentTaluk},${data.permanentDistrict}-${data.permanentPincode}`,
    },
    { label: "Address2", render: (data) => "" },
    { label: "City", render: (data) => data.permanentDistrict },
    { label: "State", render: (data) => data.state },
    { label: "Country", render: (data) => "India" },
    { label: "Pincode", render: (data) => data.permanentPincode },
    { label: "Constructs", render: (data) => "" },
    { label: "otherProduct1Applicable", render: (data) => "" },
    { label: "otherProduct1SumInsured", render: (data) => "" },
    { label: "paymentDate", render: (data) => "" },
    { label: "dateOfDisbursement", render: (data) => "" },
    // Add more columns as needed with custom render logic
  ];

  return (
    <section className="master-layout">
      <div className="master-sidebar-wrapper">
        <Sidebar
          openSidebarToggle={openSidebarToggle}
          OpenSidebar={OpenSidebar}
        />
      </div>

      <div className="master-content-wrapper">
        <div className="master-header-wrapper">
          <DashboardHeader OpenSidebar={OpenSidebar} />
        </div>

        <div className="master-card-container">
          <div className="master-search-section">
            <h1 className="TableHeaderText w-100 text-start">Insurance Report</h1>

            <button
              className={`filter-toggle-btn ${showMobileFilters ? "active" : ""}`}
              onClick={() => setShowMobileFilters(!showMobileFilters)}
            >
              <span style={{ fontWeight: 600, fontSize: "14px" }}>
                {showMobileFilters ? "Hide Filters" : "Show Filters"}
              </span>
              <MdExpandMore size={22} />
            </button>

            <div className={`filter-container ${!showMobileFilters ? "mobile-hidden" : "mobile-visible"}`}>
              <Row>
                <Col xs={12} lg={4} md={6} className="mb-2">
                  <Form.Group>
                    <Form.Label>Search Customer</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Search Name, Customer ID, Loan Acc No, Phone..."
                      value={searchTerm}
                      onChange={(e) => {
                        setSearchTerm(e.target.value);
                        setCurrentPage(1);
                      }}
                    />
                  </Form.Group>
                </Col>
                <Col xs={12} lg={2} md={6}>
                  <Form.Group>
                    <Form.Label>From Date</Form.Label>
                    <StandardizedDateInput
                      value={fromDate}
                      onChange={(event) => {
                        const val = event.target.value;
                        setFromDate(val);
                        if (val && toDate) {
                          setTimeout(() => setShowMobileFilters(false), 300);
                        }
                      }}
                    />
                  </Form.Group>
                </Col>
                <Col xs={12} lg={2} md={6}>
                  <Form.Group>
                    <Form.Label>To Date</Form.Label>
                    <StandardizedDateInput
                      value={toDate}
                      onChange={(event) => {
                        const val = event.target.value;
                        setToDate(val);
                        if (fromDate && val) {
                          setTimeout(() => setShowMobileFilters(false), 300);
                        }
                      }}
                    />
                  </Form.Group>
                </Col>
              </Row>
            </div>
          </div>

          <div className="master-list-section">
            <div className="master-table-wrapper" style={{ padding: "0 15px" }}>
              <DataTable
                columns={columns}
                data={paginationData}
                reportName="InsuranceReport"
                excelData={filteredData}
              />
            </div>

            <div className="master-pagination-wrapper">
              <CustomPagination
                totalPages={totalPages}
                currentPage={currentPage}
                paginate={paginate}
                totalItems={filteredData.length}
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default InsuranceReport;
